import React, { useState, useEffect, useRef } from 'react';

import { Swiper, SwiperSlide } from "swiper/react";
// Import Swiper styles
import "swiper/css";
import "swiper/css/effect-coverflow";
import "../../styles/WideSlider.css";
// import required modules
import { Autoplay, EffectCoverflow } from "swiper";
import { motion } from "framer-motion";
import { animationOnce, baseURL } from '../../index';
import styled from 'styled-components';

import { useFetching } from "../../hooks/useFetching";
import ProjectsService from '../../API/ProjectsService';
import { getYouTubeEmbedUrl } from '../../utils/youtube';
import Loader from '../Loader/Loader';


const WideProjectsSliderContainer = styled(motion.div)`
    margin-top: 40px;
    margin-bottom: 60px;

    @media (max-width: 767px) {
        margin-top: 30px;
        margin-bottom: 40px;
    }
`

const SlideTitle = styled.div`
    color: #FC6435;
    font-size: 1.25rem;
    font-weight: 400;
    text-transform: uppercase;
    line-height: 1.5rem;
    margin-bottom: 10px;
    /* Сдвиг на радиус скругления карточки, чтобы название начиналось с прямого участка края */
    padding-left: 30px;

    @media (max-width: 767px) {
        font-size: 1rem;
        margin-bottom: 5px;
        padding-left: 15px;
    }
`

const SlideCard = styled.div`
    position: relative;
    aspect-ratio: 16 / 9;
    border-radius: 30px;
    overflow: hidden;
    /* Фирменный фон для слайдов без картинки */
    background-color: #fc6435;

    @media (max-width: 767px) {
        aspect-ratio: 3 / 2;
        border-radius: 15px;
    }
`

const SlideBackground = styled.img`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100% !important;
    object-fit: cover;
`

/* Затемнение слева, чтобы описание читалось поверх картинки */
const SlideShade = styled.div`
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0) 65%),
                linear-gradient(0deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 25%);

    /* На мобильных текст занимает всю ширину карточки - затемняем ее целиком */
    @media (max-width: 767px) {
        background: linear-gradient(90deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.5) 60%, rgba(0,0,0,0.3) 100%),
                    linear-gradient(0deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 25%);
    }
`

const SlideContent = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 55%;
    padding: 40px;
    color: #f9f9f9;

    @media (max-width: 1023px) {
        padding: 25px;
        width: 65%;
    }
    @media (max-width: 767px) {
        padding: 15px;
        width: 100%;
    }
`

const SlideDescription = styled.div`
    font-size: 0.9rem;
    font-weight: 300;
    line-height: 1.5;
    letter-spacing: 1px;
    text-transform: uppercase;
    display: -webkit-box;
    -webkit-line-clamp: 8;
    -webkit-box-orient: vertical;
    overflow: hidden;

    @media (max-width: 1023px) {
        font-size: 0.8rem;
        -webkit-line-clamp: 6;
    }
    @media (max-width: 767px) {
        font-size: 0.7rem;
        letter-spacing: 0.5px;
        -webkit-line-clamp: 5;
    }
`

const SlideMeta = styled.div`
    margin-top: 25px;
    font-size: 0.8rem;
    font-weight: 300;
    letter-spacing: 1px;
    text-transform: uppercase;

    p {
        margin-bottom: 5px;
    }

    @media (max-width: 767px) {
        margin-top: 10px;
        font-size: 0.7rem;
    }
    @media (max-width: 519px) {
        display: none;
    }
`

const SlideVideo = styled.div`
    position: absolute;
    left: 40px;
    bottom: 65px;
    /* По макету плеер такой же ширины, как текстовый блок над ним:
       ширина SlideContent минус его боковые паддинги */
    width: calc(55% - 80px);
    aspect-ratio: 16 / 9;
    border-radius: 10px;
    overflow: hidden;

    iframe {
        display: block;
        width: 100%;
        height: 100%;
        border: 0;
    }

    @media (max-width: 1023px) {
        left: 25px;
        bottom: 55px;
        width: calc(65% - 50px);
    }
    @media (max-width: 767px) {
        display: none;
    }
`

const SlideLeftLable = styled.div`
    position: absolute;
    left: 40px;
    bottom: 20px;
    color: #f9f9f9;
    font-size: 1rem;
    font-weight: 300;
    letter-spacing: 3px;

    @media (max-width: 767px) {
        left: 15px;
        bottom: 10px;
        font-size: 0.8rem;
    }
`

const SlideRightLable = styled.div`
    position: absolute;
    right: 20px;
    bottom: 20px;
    color: #f9f9f9;
    font-size: 0.9rem;
    font-weight: 300;
    display: flex;
    flex-wrap: nowrap;

    img {
        width: 8px !important;
        align-self: center;
        margin-left: 5px;
    }

    @media (max-width: 767px) {
        right: 15px;
        bottom: 10px;
        font-size: 0.8rem;
    }
`


// Преобразует ссылку на YouTube (watch, youtu.be, shorts) в адрес для встраивания
const WideProjectsSlider = () => {
    const [projectsList, setProjectsList] = useState([]);
    const swiperRef = useRef(null);

    // Клик внутри YouTube-iframe не всплывает наружу, поэтому ловим его через потерю
    // фокуса окном: если фокус ушел в iframe нашей карусели - останавливаем autoplay
    useEffect(() => {
        const stopOnVideoClick = () => {
            const el = document.activeElement;
            if (el && el.tagName === 'IFRAME' && el.closest('.wide-slider-swiper') && swiperRef.current) {
                swiperRef.current.autoplay.stop();
            }
        };
        window.addEventListener('blur', stopOnVideoClick);
        return () => window.removeEventListener('blur', stopOnVideoClick);
    }, [])

    const [fetchProjects, isProjectsLoading] = useFetching(async () => {
        const response = await ProjectsService.getSliderProjects(10);
        setProjectsList(response.data.results);
    })

    useEffect(() => {
        fetchProjects();
    }, [])

    // Нет проектов с флажком карусели - не показываем блок вообще
    if (!isProjectsLoading && projectsList.length === 0) {
        return null;
    }

    return (
        <WideProjectsSliderContainer
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: animationOnce }}
            transition={{ duration: 0.5 }}
        >
            {isProjectsLoading
                ? <Loader marginBottom={"30px"} />
                :
                <Swiper
                    effect={"coverflow"}
                    grabCursor={true}
                    centeredSlides={true}
                    slidesPerView={"auto"}
                    coverflowEffect={{
                        rotate: 0,
                        stretch: 0,
                        depth: 400,
                        modifier: 1,
                        slideShadows: false,
                    }}
                    modules={[EffectCoverflow, Autoplay]}
                    loop={true}
                    autoplay={{
                        delay: 3000,
                        disableOnInteraction: true,
                    }}
                    onSwiper={(swiper) => swiperRef.current = swiper}
                    className="wide-slider-swiper"
                >

                    {projectsList.map((item) =>
                        <SwiperSlide key={item.pk}>
                            <a href={`/projects/${item.pk}/`}>
                                <SlideTitle className="wide-slide-content">
                                    <p dangerouslySetInnerHTML={{__html: item.title}} />
                                </SlideTitle>
                                <SlideCard className="wide-slide-card">
                                    {item.slider_image_url &&
                                        <>
                                            <SlideBackground src={baseURL + item.slider_image_url} alt="" />
                                            <SlideShade />
                                        </>
                                    }
                                    <SlideContent className="wide-slide-content">
                                        <SlideDescription dangerouslySetInnerHTML={{__html: item.description}} />
                                        <SlideMeta>
                                            <p>Category: {item.category}</p>
                                            <p>Status: {item.status}</p>
                                        </SlideMeta>
                                    </SlideContent>
                                    {item.slider_video_url && getYouTubeEmbedUrl(item.slider_video_url) &&
                                        <SlideVideo className="wide-slide-content">
                                            <iframe
                                                src={getYouTubeEmbedUrl(item.slider_video_url)}
                                                title={item.title.replace('<br>', ' ')}
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                                loading="lazy"
                                            />
                                        </SlideVideo>
                                    }
                                    <SlideLeftLable>
                                        <p>{item.category}</p>
                                    </SlideLeftLable>
                                    <SlideRightLable>
                                        <p>read more</p>
                                        <img src='/images/arrow_white.png' alt="" />
                                    </SlideRightLable>
                                </SlideCard>
                            </a>
                        </SwiperSlide>
                    )}

                </Swiper>
            }
        </WideProjectsSliderContainer>
    );
}

export default WideProjectsSlider;
